// src/pages/RealEstatePage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight, CheckCircle, Zap, DollarSign, Clock,
  Home, MessageSquare, FileText, Calendar, TrendingUp, Users,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../hooks/useLanguage";
import { captureAttribution } from "../utils/attribution";

const PAIN_ICONS = [MessageSquare, FileText, Users, Calendar, TrendingUp, Home];
const PAIN_COLORS = [
  { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
  { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
  { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  { color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  { color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
];
const TOOL_META = [
  { score: 94, color: "bg-orange-500" },
  { score: 89, color: "bg-purple-500" },
  { score: 86, color: "bg-blue-500" },
  { score: 82, color: "bg-blue-500" },
];

const CONTENT = {
  en: {
    title: "AI Blueprint for Real Estate Agents — Close More Deals, Save 8+ Hours/Week",
    metaDesc: "Get a personalized AI implementation blueprint for real estate agents. Faster lead response, automated follow-up, and listing copy in minutes. Built from 50K+ agent data.",
    heroBadge: "Built for real estate agents",
    heroTitlePart1: "Which AI Tools Will Help You",
    heroTitlePart2: "Close More Deals?",
    heroSubtitlePre: "Answer 8 questions about your real estate business. Get a personalized blueprint with the",
    heroSubtitleBold: "exact tools, setup order, and ROI projections",
    heroSubtitlePost: "— built from",
    heroSubtitleEnd: "vetted AI tools and 2026 real estate industry benchmarks.",
    heroBullets: [
      "Respond to leads in under 2 minutes — 24/7 — without lifting a finger",
      "Write listing descriptions in 3 minutes instead of 2 hours",
      "Follow up automatically for 90 days without losing a single lead",
      "Generate branded market reports in 10 minutes to drive referrals",
    ],
    heroCta: "Respond to Leads in 2 Minutes",
    heroCtaFine: "One-time — No subscription — Ready in minutes",
    painEyebrow: "Sound familiar?",
    painHeading: "The 6 Revenue Leaks Costing Agents Deals",
    painSub: "AI can fix all of these — but only if you know which tools to use and in what order. That's what the blueprint tells you.",
    painPoints: [
      { title: "Slow lead response losing deals", desc: "48% of buyers hire the first agent who responds. Average agent response time is 15+ hours. AI tools respond to leads in under 2 minutes, 24/7 — even while you're at a showing.", stat: "48% hire first responder" },
      { title: "Listing descriptions take hours to write", desc: "Agents spend 2—4 hours per listing on copy. AI writing tools produce MLS-ready descriptions in 3 minutes — tailored to the property, neighborhood, and buyer persona.", stat: "2 hrs → 3 min per listing" },
      { title: "Follow-up falls through the cracks", desc: "Only 2% of deals close on first contact. Most agents abandon leads after 2 follow-ups. AI CRM sequences follow up 8—12 times across 90 days automatically.", stat: "6x more follow-ups" },
      { title: "Scheduling showings is a grind", desc: "Coordinating showings wastes 5+ hours/week on calls and emails. AI scheduling tools let clients book directly from your link — zero back-and-forth.", stat: "5 hrs → 30 min/week" },
      { title: "Market reports take too long to create", desc: "Agents who send monthly market reports close 31% more referral deals. AI tools generate branded reports in 10 minutes instead of 2 hours.", stat: "31% more referral deals" },
      { title: "Social media is inconsistent or nonexistent", desc: "73% of sellers prefer agents with a strong social presence. AI content tools maintain consistent posting with just 20 minutes of effort per week.", stat: "73% check social first" },
    ],
    toolEyebrow: "Tool stack preview",
    toolHeading: "Tools Your Blueprint Could Recommend",
    toolSubPre: "These are examples. Your blueprint picks from",
    toolSubPost: "+ tools based on your market, volume, and budget.",
    toolStack: [
      { name: "Follow Up Boss", category: "Real-Estate-Native CRM", price: "From $69/agent/mo", why: "The default CRM for solo agents and small teams in North America. Built for real estate from day one — IDX integration, action plans, smart lists, and AI-driven lead routing. Integrates directly with Zillow, Realtor.com, and most MLS feeds." },
      { name: "kvCORE", category: "Real-Estate Platform + CRM", price: "Custom quote (brokerage-tier)", why: "Inside Real Estate's all-in-one platform — IDX website, CRM, automated nurture, and AI behavioral lead scoring. Quoted at the brokerage level. Strong fit for teams of 5+ or solo agents under a brokerage that offers it." },
      { name: "Lofty (formerly Chime)", category: "AI Lead Nurture & IDX", price: "From $499/mo", why: "AI lead nurture, IDX site, and team transaction management. Behavioral AI that scores leads based on engagement and triggers personalized follow-ups. Strong fit for teams that want a unified platform without going to kvCORE's price point." },
      { name: "Jasper AI", category: "Listing Copy & Content (supplemental)", price: "From $39/mo", why: "Supplemental copywriting layer — MLS descriptions, social posts, email campaigns, and market reports. Pair with your real-estate CRM rather than replace it. Learns your brand voice over time." },
    ],
    matchLabel: "match",
    toolFootnotePre: "Your blueprint selects 5—8 tools from a database of",
    toolFootnotePost: "+, ranked for your specific business.",
    getEyebrow: "What you get",
    getHeadingPre: "Your $",
    getHeadingPost: " Real Estate AI Blueprint Includes",
    whatYouGet: [
      { title: "Your exact real estate AI tool stack", desc: "5—8 tools matched to your volume, budget, and specific bottlenecks. Whether you're solo or managing a team of 10." },
      { title: "12-week implementation roadmap", desc: "Phase 1 fixes your biggest leak — usually lead response time or follow-up consistency. Week-by-week, not an overwhelming list." },
      { title: "Custom prompt library for real estate", desc: "15—30 prompts for listing descriptions, social posts, email follow-ups, market report copy, and offer letters. Ready to paste." },
      { title: "ROI projections for your business", desc: "Expected deals recovered from faster response, hours saved on admin, and referrals from consistent marketing." },
      { title: "Risk guide and what to avoid", desc: "What goes wrong when agents adopt too many tools at once. How to start with one win and build from there without disrupting your business." },
      { title: "First 30 days checklist", desc: "Day-by-day tasks to see results fast — faster lead response, less time on listing copy, or more consistent long-term follow-up." },
    ],
    testimonialLaunch: "We launched recently — real customer results coming soon.",
    testimonialGuarantee: "60-day money-back guarantee if it doesn't deliver value.",
    bottomHeading1: "Stop Losing Deals to Faster Agents.",
    bottomHeading2: "Start Running a Smarter Business.",
    bottomSub: "Answer 8 questions about your real estate business. Get a blueprint with your exact tool stack, setup order, and week-by-week roadmap. Ready in minutes.",
    bottomCta: "Get My Real Estate AI Blueprint",
    bottomFineReady: "Ready in minutes",
    bottomFineOneTime: "One-time $",
    bottomFineNoSub: "No subscription",
    preview: {
      eyebrow: "AI Implementation Blueprint",
      kicker: "Real Estate — Agents & Brokers",
      reportReady: "✓ Report Ready",
      roiLabel: "Year 1 ROI",
      hoursSavedLabel: "Saved/week",
      toolsLabel: "Recommended",
      hrsUnit: "hrs",
      toolsUnit: "tools",
      topToolsEyebrow: "Top Tools For Your Business",
      toolBadges: ["CRM — $69/agent/mo", "Platform — Custom quote", "Nurture — $499/mo"],
      matchShort: "match",
      roadmapEyebrow: "12-Week Roadmap",
      phaseLabels: ["Phase 1\nLead Response", "Phase 2\nContent & Copy", "Phase 3\nScale & Referrals"],
      floatingBadge1: "✨ Built in 3 min",
      floatingBadge2: "Agent-optimized",
    },
    faqEyebrow: "Frequently asked",
    faqHeading: "Before you build your blueprint",
    faq: [
      { q: "I'm a solo agent / team lead / luxury / investor-focused / commercial. Does this still apply?", a: "Yes. The blueprint asks about your niche, transaction volume, and biggest bottleneck, then ranks tools to fit. Solo agents usually get lead-response and CRM AI; teams get routing and coaching tools; luxury agents get content and video AI; commercial and investor-focused agents get underwriting and comp-analysis tools." },
      { q: "How long does the blueprint take to implement?", a: "It's a 12-week roadmap built for agents with 2—4 hours a week to invest. Phase 1 targets your biggest leak first — usually slow lead response or dead-in-the-funnel nurture — and most agents see measurable results (faster response, more appointments booked) in 2—3 weeks before Phase 2." },
      { q: "I've tried AI tools and they all felt generic or didn't integrate. Why is this different?", a: "The blueprint only ranks tools that integrate with the CRM and MLS workflow you already use (Follow Up Boss, kvCORE, Chime, BoomTown, etc.) and flags anything that forces a second login or copy-paste. Each tool is ranked by fit to your niche, not by marketing spend." },
      { q: "Is my client and lead data safe?", a: "Every tool recommendation includes a data-handling note — where lead data is stored, whether it's used to train models, and whether the vendor shares data with third parties. Tools that train on your client data by default are flagged, not quietly recommended." },
      { q: "What if the blueprint doesn't fit my business?", a: "60-day money-back guarantee, no questions asked. Email us within 60 days and we refund in full. You keep the blueprint. We'd rather you leave happy than keep $149 on a report you couldn't use." },
    ],
  },
  es: {
    title: "Blueprint de IA para Agentes Inmobiliarios — Cierra Más Operaciones, Ahorra 8+ Horas/Semana",
    metaDesc: "Recibe un blueprint personalizado de implementación de IA para agentes inmobiliarios. Respuesta más rápida a leads, seguimiento automatizado y descripciones de propiedades en minutos. Creado con datos de más de 50.000 agentes.",
    heroBadge: "Diseñado para agentes inmobiliarios",
    heroTitlePart1: "¿Qué Herramientas de IA Te Ayudarán A",
    heroTitlePart2: "Cerrar Más Operaciones?",
    heroSubtitlePre: "Responde 8 preguntas sobre tu negocio inmobiliario. Obtén un blueprint personalizado con las",
    heroSubtitleBold: "herramientas exactas, el orden de implementación y las proyecciones de ROI",
    heroSubtitlePost: "— construido a partir de",
    heroSubtitleEnd: "herramientas de IA validadas y los benchmarks del sector inmobiliario 2026.",
    heroBullets: [
      "Responde a leads en menos de 2 minutos — 24/7 — sin mover un dedo",
      "Redacta descripciones de propiedades en 3 minutos en lugar de 2 horas",
      "Haz seguimiento automático durante 90 días sin perder un solo lead",
      "Genera reportes de mercado con tu marca en 10 minutos para impulsar referidos",
    ],
    heroCta: "Responde a Leads en 2 Minutos",
    heroCtaFine: "Pago único — Sin suscripción — Listo en minutos",
    painEyebrow: "¿Te suena familiar?",
    painHeading: "Las 6 Fugas de Ingresos Que Están Costando Operaciones",
    painSub: "La IA puede resolver todo esto — pero solo si sabes qué herramientas usar y en qué orden. Eso es lo que te dice el blueprint.",
    painPoints: [
      { title: "Respuesta lenta a leads pierde operaciones", desc: "El 48% de los compradores contrata al primer agente que responde. El tiempo promedio de respuesta es de 15+ horas. Las herramientas de IA responden en menos de 2 minutos, 24/7 — incluso mientras estás en una visita.", stat: "48% contrata al primero" },
      { title: "Las descripciones de propiedades toman horas", desc: "Los agentes dedican 2—4 horas por propiedad a redactar el copy. Las herramientas de escritura con IA producen descripciones listas para MLS en 3 minutos — ajustadas a la propiedad, el barrio y el perfil del comprador.", stat: "2 hrs → 3 min por listing" },
      { title: "El seguimiento se pierde en el camino", desc: "Solo el 2% de las operaciones cierra en el primer contacto. La mayoría de los agentes abandona al lead tras 2 seguimientos. Los CRM con IA hacen 8—12 seguimientos automáticos a lo largo de 90 días.", stat: "6x más seguimientos" },
      { title: "Coordinar visitas es agotador", desc: "Coordinar visitas consume 5+ horas/semana entre llamadas y correos. Las herramientas de IA permiten que los clientes reserven desde tu enlace — cero idas y vueltas.", stat: "5 hrs → 30 min/semana" },
      { title: "Los reportes de mercado tardan demasiado", desc: "Los agentes que envían reportes mensuales cierran 31% más operaciones por referidos. Las herramientas de IA generan reportes con tu marca en 10 minutos en lugar de 2 horas.", stat: "31% más referidos" },
      { title: "Las redes sociales son inconsistentes o inexistentes", desc: "El 73% de los vendedores prefiere agentes con presencia social fuerte. Las herramientas de contenido con IA mantienen publicaciones consistentes con solo 20 minutos a la semana.", stat: "73% revisa redes primero" },
    ],
    toolEyebrow: "Vista previa del stack",
    toolHeading: "Herramientas Que Tu Blueprint Podría Recomendar",
    toolSubPre: "Son ejemplos. Tu blueprint elige entre",
    toolSubPost: "+ herramientas según tu mercado, volumen y presupuesto.",
    toolStack: [
      { name: "Follow Up Boss", category: "CRM Nativo de Real Estate", price: "Desde $69/agente/mes", why: "El CRM por defecto para agentes solo y equipos pequeños en Norteamérica. Hecho para real estate desde el día uno — integración IDX, planes de acción, listas inteligentes y enrutamiento de leads con IA. Integra directo con Zillow, Realtor.com y la mayoría de feeds MLS." },
      { name: "kvCORE", category: "Plataforma Real Estate + CRM", price: "Cotización personalizada (nivel brokerage)", why: "Plataforma todo-en-uno de Inside Real Estate — sitio IDX, CRM, nutrición automática y scoring de leads con IA conductual. Cotizado a nivel brokerage. Encaja para equipos de 5+ o agentes solo bajo un brokerage que lo ofrezca." },
      { name: "Lofty (antes Chime)", category: "Nutrición de Leads IA + IDX", price: "Desde $499/mes", why: "Nutrición de leads con IA, sitio IDX y gestión de transacciones para equipos. IA conductual que califica leads según engagement y dispara seguimientos personalizados. Encaja para equipos que quieren plataforma unificada sin llegar al precio de kvCORE." },
      { name: "Jasper AI", category: "Copy de Propiedades y Contenido (suplementario)", price: "Desde $39/mes", why: "Capa suplementaria de copy — descripciones MLS, posts sociales, campañas de email y reportes de mercado. Combinar con tu CRM de real estate, no reemplazarlo. Aprende tu voz de marca con el tiempo." },
    ],
    matchLabel: "compatibilidad",
    toolFootnotePre: "Tu blueprint selecciona 5—8 herramientas de una base de datos de",
    toolFootnotePost: "+, clasificadas para tu negocio específico.",
    getEyebrow: "Lo que obtienes",
    getHeadingPre: "Tu Blueprint de IA Inmobiliaria por $",
    getHeadingPost: " Incluye",
    whatYouGet: [
      { title: "Tu stack exacto de herramientas de IA inmobiliaria", desc: "5—8 herramientas elegidas según tu volumen, presupuesto y cuellos de botella específicos. Ya sea que trabajes solo o lideres un equipo de 10." },
      { title: "Hoja de ruta de 90 días", desc: "La Fase 1 resuelve tu mayor fuga — normalmente el tiempo de respuesta o la consistencia del seguimiento. Semana a semana, no una lista abrumadora." },
      { title: "Biblioteca de prompts para inmobiliaria", desc: "15—30 prompts para descripciones de propiedades, posts sociales, emails de seguimiento, reportes de mercado y cartas de oferta. Listos para pegar." },
      { title: "Proyecciones de ROI para tu negocio", desc: "Operaciones que recuperarás con respuesta más rápida, horas ahorradas en administración y referidos por marketing consistente." },
      { title: "Guía de riesgos y qué evitar", desc: "Qué sale mal cuando los agentes adoptan demasiadas herramientas a la vez. Cómo empezar con una victoria y crecer desde ahí sin alterar tu negocio." },
      { title: "Checklist de los primeros 30 días", desc: "Tareas día a día para ver resultados rápido — respuesta más rápida a leads, menos tiempo en copy de propiedades o seguimiento más consistente a largo plazo." },
    ],
    testimonialLaunch: "Acabamos de lanzar — resultados reales de clientes muy pronto.",
    testimonialGuarantee: "Garantía de reembolso de 60 días si no aporta valor.",
    bottomHeading1: "Deja de Perder Operaciones Con Agentes Más Rápidos.",
    bottomHeading2: "Empieza a Dirigir un Negocio Más Inteligente.",
    bottomSub: "Responde 8 preguntas sobre tu negocio inmobiliario. Obtén un blueprint con tu stack exacto, orden de implementación y hoja de ruta semana a semana. Listo en minutos.",
    bottomCta: "Arma Mi Blueprint Inmobiliario",
    bottomFineReady: "Listo en minutos",
    bottomFineOneTime: "Pago único $",
    bottomFineNoSub: "Sin suscripción",
    preview: {
      eyebrow: "Blueprint de Implementación de IA",
      kicker: "Inmobiliaria — Agentes y Brokers",
      reportReady: "✓ Informe Listo",
      roiLabel: "ROI Año 1",
      hoursSavedLabel: "Ahorro/semana",
      toolsLabel: "Recomendadas",
      hrsUnit: "hrs",
      toolsUnit: "herramientas",
      topToolsEyebrow: "Top Herramientas Para Tu Negocio",
      toolBadges: ["CRM — $69/agente/mes", "Plataforma — Cotización", "Nutrición — $499/mes"],
      matchShort: "match",
      roadmapEyebrow: "Hoja de Ruta 12 Semanas",
      phaseLabels: ["Fase 1\nRespuesta a Leads", "Fase 2\nContenido y Copy", "Fase 3\nEscala y Referidos"],
      floatingBadge1: "✨ Listo en 3 min",
      floatingBadge2: "Optimizado para agentes",
    },
    faqEyebrow: "Preguntas frecuentes",
    faqHeading: "Antes de armar tu blueprint",
    faq: [
      { q: "Soy agente solo / líder de equipo / de lujo / enfocado en inversores / comercial. ¿Igual aplica?", a: "Sí. El blueprint te pregunta tu nicho, volumen de transacciones y mayor cuello de botella, y rankea las herramientas a tu medida. Los agentes solos reciben IA de respuesta a leads y CRM; los equipos reciben ruteo y coaching; los de lujo reciben contenido y video IA; los de comercial e inversores reciben underwriting y análisis de comparables." },
      { q: "¿Cuánto tarda implementar el blueprint?", a: "Es una hoja de ruta de 12 semanas pensada para agentes con 2—4 horas a la semana. La Fase 1 ataca tu mayor fuga primero — normalmente respuesta lenta a leads o nurture muerto — y la mayoría ve resultados medibles (respuesta más rápida, más citas agendadas) en 2—3 semanas antes de pasar a la Fase 2." },
      { q: "Ya probé herramientas de IA y sentí que eran genéricas o no se integraban. ¿Por qué esto es distinto?", a: "El blueprint solo rankea herramientas que se integran con el CRM y MLS que ya usas (Follow Up Boss, kvCORE, Chime, BoomTown, etc.) y marca cualquiera que obligue a un segundo login o copy-paste. Cada herramienta se rankea por ajuste a tu nicho, no por su presupuesto de marketing." },
      { q: "¿Están seguros los datos de mis clientes y leads?", a: "Cada recomendación incluye una nota de manejo de datos — dónde se almacenan los leads, si se usan para entrenar modelos y si el proveedor comparte con terceros. Las herramientas que entrenan con datos de cliente por defecto se marcan, no se recomiendan en silencio." },
      { q: "¿Y si el blueprint no encaja con mi negocio?", a: "Garantía de devolución de 60 días, sin preguntas. Nos escribes dentro de los 60 días y te devolvemos todo. Te quedas con el blueprint. Preferimos que te vayas contento antes que cobrarte $149 por un reporte que no pudiste usar." },
    ],
  },
  tr: {
    title: "Emlak Danışmanları için Yapay Zeka Planı — Daha Çok Satış Kapatın, Haftada 8+ Saat Kazanın",
    metaDesc: "Emlak danışmanları için kişiselleştirilmiş bir yapay zeka uygulama planı alın. Daha hızlı müşteri yanıtı, otomatik takip ve dakikalar içinde ilan metinleri. 50.000+ danışman verisiyle hazırlandı.",
    heroBadge: "Emlak danışmanları için tasarlandı",
    heroTitlePart1: "Hangi Yapay Zeka Araçları",
    heroTitlePart2: "Daha Fazla Satış Kapatmanıza Yardım Eder?",
    heroSubtitlePre: "Emlak işiniz hakkında 8 soruyu yanıtlayın.",
    heroSubtitleBold: "tam araç listesi, kurulum sırası ve ROI projeksiyonları",
    heroSubtitlePost: "içeren kişiselleştirilmiş bir plan edinin —",
    heroSubtitleEnd: "onaylı yapay zeka aracı ve 2026 emlak sektörü kıyaslamalarıyla hazırlandı.",
    heroBullets: [
      "Leadlere 2 dakika içinde yanıt verin — 7/24 — parmağınızı bile kıpırdatmadan",
      "İlan açıklamalarını 2 saat yerine 3 dakikada yazın",
      "90 gün boyunca otomatik takip yapın, tek bir lead bile kaçırmayın",
      "Referansları artırmak için markalı pazar raporlarını 10 dakikada oluşturun",
    ],
    heroCta: "Leadlere 2 Dakikada Yanıt Verin",
    heroCtaFine: "Tek seferlik — Abonelik yok — Dakikalar içinde hazır",
    painEyebrow: "Tanıdık geliyor mu?",
    painHeading: "Danışmanlara Satış Kaybettiren 6 Gelir Sızıntısı",
    painSub: "Yapay zeka bunların hepsini çözebilir — ama yalnızca hangi araçları hangi sırayla kullanacağınızı bilirseniz. Plan size tam olarak bunu söyler.",
    painPoints: [
      { title: "Yavaş lead yanıtı satış kaybettiriyor", desc: "Alıcıların %48'i ilk yanıt veren danışmanı tercih ediyor. Ortalama yanıt süresi 15+ saat. Yapay zeka araçları 2 dakikadan kısa sürede, 7/24 yanıt verir — siz bir evi gezdirirken bile.", stat: "%48 ilk yanıt vereni seçer" },
      { title: "İlan açıklamaları saatler alıyor", desc: "Danışmanlar her ilan için 2—4 saat yazıya harcıyor. Yapay zeka yazı araçları MLS'e hazır açıklamaları 3 dakikada üretir — mülke, semte ve alıcı profiline göre özelleştirilir.", stat: "İlan başına 2 saat → 3 dk" },
      { title: "Takip çatlaklardan kayboluyor", desc: "Satışların yalnızca %2'si ilk temasta kapanır. Danışmanların çoğu 2 takiptten sonra vazgeçer. Yapay zeka destekli CRM'ler 90 gün boyunca otomatik olarak 8—12 kez takip eder.", stat: "6 kat daha çok takip" },
      { title: "Ev gezmeleri koordine etmek yorucu", desc: "Ev gezmelerini koordine etmek haftada 5+ saat telefon ve e-posta alır. Yapay zeka randevu araçları, müşterilerin doğrudan linkinizden rezervasyon yapmasını sağlar — gidip gelme yok.", stat: "Haftada 5 saat → 30 dk" },
      { title: "Pazar raporları hazırlamak çok uzun sürüyor", desc: "Aylık pazar raporu gönderen danışmanlar %31 daha fazla referans satışı kapatıyor. Yapay zeka araçları markalı raporları 2 saat yerine 10 dakikada üretir.", stat: "%31 daha çok referans satış" },
      { title: "Sosyal medya tutarsız ya da yok", desc: "Satıcıların %73'ü güçlü sosyal medya varlığı olan danışmanı tercih ediyor. Yapay zeka içerik araçları haftada yalnızca 20 dakikayla tutarlı paylaşımları sürdürür.", stat: "%73 önce sosyalı inceler" },
    ],
    toolEyebrow: "Araç önizlemesi",
    toolHeading: "Planınızın Önerebileceği Araçlar",
    toolSubPre: "Bunlar örnek. Planınız, pazarınıza, hacminize ve bütçenize göre",
    toolSubPost: "+ araç arasından seçim yapar.",
    toolStack: [
      { name: "Follow Up Boss", category: "Real Estate'e Özel CRM", price: "Ajan başına aylık $69'dan başlar", why: "Kuzey Amerika'da tek başına çalışan ajanlar ve küçük ekipler için varsayılan CRM. İlk günden real estate için yapıldı — IDX entegrasyonu, eylem planları, akıllı listeler ve AI destekli lead yönlendirme. Zillow, Realtor.com ve çoğu MLS feed'i ile doğrudan entegre." },
      { name: "kvCORE", category: "Real Estate Platform + CRM", price: "Özel teklif (brokerage seviyesi)", why: "Inside Real Estate'in tümleşik platformu — IDX sitesi, CRM, otomatik lead yetiştirme ve davranışsal AI lead scoring. Brokerage seviyesinde teklif edilir. 5+ kişilik ekipler ya da bunu sunan brokerage altındaki tek başına ajanlar için uygundur." },
      { name: "Lofty (eski Chime)", category: "AI Lead Yetiştirme + IDX", price: "Aylık $499'dan başlar", why: "AI lead yetiştirme, IDX sitesi ve ekip işlem yönetimi. Etkileşime göre lead'leri puanlayan ve kişiselleştirilmiş takipler tetikleyen davranışsal AI. kvCORE'un fiyat noktasına çıkmak istemeyen ekipler için iyi bir uyum." },
      { name: "Jasper AI", category: "İlan Metni ve İçerik (yardımcı)", price: "Aylık $39'dan başlar", why: "Yardımcı kopya katmanı — MLS açıklamaları, sosyal paylaşımlar, e-posta kampanyaları ve pazar raporları. Real estate CRM'inizi değiştirmek değil, yanına eklemek için. Zamanla marka sesinizi öğrenir." },
    ],
    matchLabel: "uyum",
    toolFootnotePre: "Planınız, işinize göre sıralanmış",
    toolFootnotePost: "+ araçlık veri tabanından 5—8 araç seçer.",
    getEyebrow: "Neler alıyorsunuz",
    getHeadingPre: "$",
    getHeadingPost: "'lık Emlak Yapay Zeka Planınız Şunları İçerir",
    whatYouGet: [
      { title: "Tam emlak yapay zeka araç listeniz", desc: "Hacminize, bütçenize ve darboğazlarınıza göre seçilmiş 5—8 araç. Tek başınıza çalışıyor olun ya da 10 kişilik bir ekip yönetin." },
      { title: "90 günlük uygulama yol haritası", desc: "Faz 1, en büyük sızıntınızı giderir — genellikle lead yanıt süresi ya da takip tutarlılığı. Hafta hafta, bunaltıcı bir liste değil." },
      { title: "Emlak sektörüne özel prompt kütüphanesi", desc: "İlan açıklamaları, sosyal paylaşımlar, e-posta takipleri, pazar raporu metinleri ve teklif mektupları için 15—30 prompt. Kopyala-yapıştır hazır." },
      { title: "İşiniz için ROI projeksiyonları", desc: "Daha hızlı yanıtla geri kazanılan satışlar, yönetimde biriken saatler ve tutarlı pazarlamadan gelen referanslar." },
      { title: "Risk rehberi ve nelerden kaçınmalı", desc: "Danışmanlar aynı anda çok fazla araç benimsediğinde neler ters gider. Tek bir kazanımla başlayıp işinizi aksatmadan nasıl büyüyeceğiniz." },
      { title: "İlk 30 gün kontrol listesi", desc: "Hızlı sonuçlar için gün gün görevler — daha hızlı lead yanıtı, ilan metinlerinde daha az zaman ya da uzun vadede daha tutarlı takip." },
    ],
    testimonialLaunch: "Yeni başladık — gerçek müşteri sonuçları yakında.",
    testimonialGuarantee: "Değer getirmezse 60 gün para iade garantisi.",
    bottomHeading1: "Daha Hızlı Danışmanlara Satış Kaybetmeyi Bırakın.",
    bottomHeading2: "Daha Akıllı Bir İş Yürütmeye Başlayın.",
    bottomSub: "Emlak işiniz hakkında 8 soruyu yanıtlayın. Tam araç listeniz, kurulum sıranız ve hafta hafta yol haritanızla planınızı alın. Dakikalar içinde hazır.",
    bottomCta: "Emlak AI Blueprint'imi Al",
    bottomFineReady: "Dakikalar içinde hazır",
    bottomFineOneTime: "Tek seferlik $",
    bottomFineNoSub: "Abonelik yok",
    preview: {
      eyebrow: "Yapay Zeka Uygulama Planı",
      kicker: "Emlak — Danışmanlar ve Brokerlar",
      reportReady: "✓ Rapor Hazır",
      roiLabel: "1. Yıl ROI",
      hoursSavedLabel: "Haftalık tasarruf",
      toolsLabel: "Önerilen",
      hrsUnit: "saat",
      toolsUnit: "araç",
      topToolsEyebrow: "İşiniz İçin En İyi Araçlar",
      toolBadges: ["CRM — Ajan/ay $69", "Platform — Özel teklif", "Yetiştirme — $499/ay"],
      matchShort: "uyum",
      roadmapEyebrow: "12 Haftalık Yol Haritası",
      phaseLabels: ["Faz 1\nLead Yanıtı", "Faz 2\nİçerik ve Metin", "Faz 3\nÖlçek ve Referans"],
      floatingBadge1: "✨ 3 dakikada hazır",
      floatingBadge2: "Danışman odaklı",
    },
    faqEyebrow: "Sıkça sorulanlar",
    faqHeading: "Blueprint'inizi oluşturmadan önce",
    faq: [
      { q: "Solo danışmanım / ekip lideriyim / lüks segment / yatırımcı odaklı / ticariyim. Yine de işe yarar mı?", a: "Evet. Blueprint size nişinizi, işlem hacminizi ve en büyük darboğazınızı sorar ve araçları buna göre sıralar. Solo danışmanlar genellikle lead yanıtı ve CRM AI'ı alır; ekipler yönlendirme ve koçluk araçları alır; lüks danışmanlar içerik ve video AI'ı alır; ticari ve yatırımcı odaklı danışmanlar underwriting ve emsal analiz araçları alır." },
      { q: "Blueprint'i uygulamak ne kadar sürer?", a: "Haftada 2—4 saat ayırabilen danışmanlar için tasarlanmış 12 haftalık bir yol haritasıdır. 1. Faz önce en büyük kaybınıza — genellikle yavaş lead yanıtı veya ölü kalmış nurture — odaklanır ve çoğu danışman 2. Faz'a geçmeden önce 2—3 hafta içinde ölçülebilir sonuç (daha hızlı yanıt, daha fazla randevu) görür." },
      { q: "AI araçları denedim ama hep jenerik hissetti veya entegre olmadılar. Bunun farkı ne?", a: "Blueprint yalnızca zaten kullandığınız CRM ve MLS iş akışına (Follow Up Boss, kvCORE, Chime, BoomTown vb.) entegre olan araçları sıralar ve ikinci bir giriş veya kopyala-yapıştır gerektiren her şeyi işaretler. Her araç pazarlama bütçesine göre değil, nişinize uygunluğa göre sıralanır." },
      { q: "Müşteri ve lead verilerim güvende mi?", a: "Her öneride veri işleme notu vardır — lead verilerinin nerede saklandığı, model eğitiminde kullanılıp kullanılmadığı ve sağlayıcının üçüncü taraflarla paylaşıp paylaşmadığı. Varsayılan olarak müşteri verisiyle eğitim yapan araçlar işaretlenir, sessizce önerilmez." },
      { q: "Blueprint işime uymazsa ne olur?", a: "60 gün içinde koşulsuz para iade garantisi. 60 gün içinde bize yazarsınız, tam iade yaparız. Blueprint sizde kalır. 149 dolarınızı tutup mutsuz göndermektense kullanamayacağınız bir rapor için iade etmeyi tercih ederiz." },
    ],
  },
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const RealEstateBlueprintPreview = ({ c }) => (
  <div className="relative select-none">
    <div className="absolute -inset-4 bg-gradient-to-br from-violet-600/20 via-indigo-600/20 to-purple-600/20 rounded-3xl blur-2xl" />
    <div className="relative rounded-2xl overflow-hidden bg-slate-800/90 border border-white/10 shadow-2xl backdrop-blur-sm">
      <div className="bg-slate-900 px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-violet-400 font-semibold tracking-widest uppercase mb-0.5">{c.preview.eyebrow}</div>
          <div className="text-sm font-bold text-white">{c.preview.kicker}</div>
        </div>
        <span className="text-[10px] font-semibold bg-violet-500/20 text-violet-400 border border-violet-500/30 px-2.5 py-1 rounded-full">{c.preview.reportReady}</span>
      </div>
      <div className="grid grid-cols-3 border-b border-white/10">
        {[["$12–52K", c.preview.roiLabel], [`8 ${c.preview.hrsUnit}`, c.preview.hoursSavedLabel], [`5–8 ${c.preview.toolsUnit}`, c.preview.toolsLabel]].map(([val, lab]) => (
          <div key={lab} className="px-4 py-3 text-center border-r last:border-r-0 border-white/10">
            <div className="text-base font-bold text-violet-400">{val}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{lab}</div>
          </div>
        ))}
      </div>
      <div className="p-4 space-y-2.5">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.topToolsEyebrow}</div>
        {[
          { name: "Follow Up Boss", badge: c.preview.toolBadges[0], score: 94, color: "bg-blue-500" },
          { name: "kvCORE", badge: c.preview.toolBadges[1], score: 89, color: "bg-cyan-500" },
          { name: "Lofty", badge: c.preview.toolBadges[2], score: 85, color: "bg-purple-500" },
        ].map((tool) => (
          <div key={tool.name} className="bg-slate-900/80 rounded-xl p-3 border border-white/10/40">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-white">{tool.name}</span>
              <span className="text-[10px] text-slate-400">{tool.badge}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div className={`h-full ${tool.color} rounded-full`} initial={{ width: 0 }} animate={{ width: `${tool.score}%` }} transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }} />
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}% {c.preview.matchShort}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.roadmapEyebrow}</div>
        <div className="flex gap-2">
          {c.preview.phaseLabels.map((p, i) => (
            <div key={p} className={`flex-1 text-center rounded-lg py-2 px-1 text-[10px] font-medium border ${i === 0 ? "bg-violet-500/20 border-violet-500/40 text-violet-400" : "bg-white/8 border-white/10 text-slate-500"}`}>
              {p.split("\n").map((line, j) => <div key={j}>{line}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>
    <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-4 -right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-blue-500/40">
      {c.preview.floatingBadge1}
    </motion.div>
    <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="absolute -bottom-3 -left-4 bg-violet-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-violet-600/40">
      {c.preview.floatingBadge2}
    </motion.div>
  </div>
);

const RealEstatePage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;
  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 348;
  const [currentPrice, setCurrentPrice] = useState(149);

  useEffect(() => {
    captureAttribution();  // R31.7e: paid-traffic landing capture
    const base = process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com';
    fetch(`${base}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  const goToAudit = () => navigate("/audit?industry=real_estate");

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <SEOHead
        path="/real-estate"
        lang={lang}
        title={c.title}
        description={c.metaDesc}
        faqItems={c.faq || c.faqs}
        industry="Real Estate"
      />
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-indigo-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="max-w-7xl mx-auto relative">
          {/* R31.7e: hero block paints immediately (no Framer fade-in). */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/30 text-violet-400 text-sm font-semibold px-4 py-2 rounded-full mb-8">
                <span className="text-base">🏠</span> {c.heroBadge}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
                <span className="text-white">{c.heroTitlePart1}</span>
                <br />
                <span className="bg-gradient-to-r from-violet-400 via-indigo-400 to-blue-400 bg-clip-text text-transparent">{c.heroTitlePart2}</span>
              </h1>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl">
                {c.heroSubtitlePre}{" "}
                <span className="text-white font-semibold">{c.heroSubtitleBold}</span>{" "}
                {c.heroSubtitlePost} {displayCount} {c.heroSubtitleEnd}
              </p>
              <ul className="space-y-3 mb-10">
                {c.heroBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <Button onClick={goToAudit} size="lg" className="bg-violet-500 hover:bg-violet-400 text-white font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-violet-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
                  {c.heroCta} — ${currentPrice} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-xs text-slate-500 self-center">{c.heroCtaFine}</p>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.7 }} className="hidden lg:block">
              <RealEstateBlueprintPreview c={c} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-violet-400 font-semibold tracking-widest uppercase mb-3">{c.painEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.painHeading}</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">{c.painSub}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.painPoints.map((pp, idx) => {
                const Icon = PAIN_ICONS[idx];
                const cls = PAIN_COLORS[idx];
                return (
                  <motion.div key={pp.title} variants={fadeUp} className={`rounded-2xl border p-6 ${cls.bg} ${cls.border}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls.bg} border ${cls.border} mb-4`}>
                      <Icon className={`w-5 h-5 ${cls.color}`} />
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{pp.title}</h3>
                    <p className="text-slate-400 text-sm mb-3 leading-relaxed">{pp.desc}</p>
                    <div className={`text-xs font-bold ${cls.color}`}>{pp.stat}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TOOL PREVIEW */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-indigo-400 font-semibold tracking-widest uppercase mb-3">{c.toolEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.toolHeading}</h2>
              <p className="text-slate-400 max-w-xl mx-auto">{c.toolSubPre} {displayCount}{c.toolSubPost}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.toolStack.map((tool, idx) => {
                const meta = TOOL_META[idx];
                return (
                  <motion.div key={tool.name} variants={fadeUp} className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-white font-bold text-lg">{tool.name}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{tool.category} — {tool.price}</div>
                      </div>
                      <div className="flex items-center gap-1.5 bg-slate-900/60 border border-white/10 rounded-full px-3 py-1">
                        <div className={`w-2 h-2 rounded-full ${meta.color}`} />
                        <span className="text-xs text-slate-400 font-semibold">{meta.score}% {c.matchLabel}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{tool.why}</p>
                    <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div className={`h-full ${meta.color} rounded-full`} initial={{ width: 0 }} whileInView={{ width: `${meta.score}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3, ease: "easeOut" }} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <motion.div variants={fadeUp} className="text-center mt-10">
              <p className="text-slate-500 text-sm">{c.toolFootnotePre} {displayCount}{c.toolFootnotePost}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-purple-400 font-semibold tracking-widest uppercase mb-3">{c.getEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.getHeadingPre}{currentPrice}{c.getHeadingPost}</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.whatYouGet.map((item) => (
                <motion.div key={item.title} variants={fadeUp} className="flex gap-4 p-5 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <div className="w-8 h-8 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-violet-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-1">{item.title}</div>
                    <div className="text-slate-400 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS — awaiting real customer results */}
      <section className="py-16 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-slate-500 text-sm py-8">
            <span className="text-slate-600">{c.testimonialGuarantee}</span>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">{c.faqEyebrow}</div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">{c.faqHeading}</h2>
          </div>
          <div className="space-y-3">
            {c.faq.map((item, i) => (
              <details key={i} className="group bg-slate-900/70 border border-white/10 rounded-2xl px-5 py-4 transition-colors hover:border-blue-500/30">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="text-white font-semibold text-sm leading-snug">{item.q}</span>
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl flex-shrink-0">+</span>
                </summary>
                <p className="text-slate-400 text-sm leading-relaxed mt-3 pt-3 border-t border-white/8">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-24 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center mx-auto mb-8">
              <Home className="w-8 h-8 text-violet-400" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              {c.bottomHeading1}
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">{c.bottomHeading2}</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">{c.bottomSub}</p>
            <Button onClick={goToAudit} size="lg" className="bg-violet-500 hover:bg-violet-400 text-white font-black text-xl px-12 py-7 rounded-2xl shadow-xl shadow-violet-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
              {c.bottomCta} — ${currentPrice} <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-6 mt-8">
              {[{ icon: Clock, text: c.bottomFineReady }, { icon: DollarSign, text: `${c.bottomFineOneTime}${currentPrice}` }, { icon: CheckCircle, text: c.bottomFineNoSub }].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-slate-500 text-sm"><Icon className="w-4 h-4" /><span>{text}</span></div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RealEstatePage;
